import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../../../styles/detailStyles.css';
import PCLayout from '../../PCLayout';

const ProjectsDetailPage = ({ isDarkMode, toggleTheme }) => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);

    useEffect(() => {
        fetch('/data/projects.json')
            .then(response => response.json())
            .then(data => setProjects(data))
            .catch(error => console.error('Error fetching projects data:', error));
    }, []);

    // Handle card click
    const handleCardClick = (project) => {
        setSelectedProject(project);
    };

    // Handle modal close
    const handleCloseModal = () => {
        setSelectedProject(null);
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleCloseModal();
            }
        };

        if (selectedProject) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [selectedProject]);

    const handleBack = () => {
        navigate('/misc', { state: { targetSection: 'projects' } });
    };

    return (
        <PCLayout
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            activePage="projects"
        >
            <div className="detailPageContent">
                <div className="inlineBackButtonContainer">
                    <button onClick={handleBack} className="inlineBackButton">
                        <i className="fa-solid fa-arrow-left"></i> Back
                    </button>
                </div>
                <div className="cardGrid">
                    {projects.map((project) => (
                        <div
                            className="card projectCard"
                            key={project.id}
                            onClick={() => handleCardClick(project)}
                        >
                            <div className="cardImage">
                                <img src={project.image || "/images/placeholder/project.png"} alt={project.title} />
                                <div className="imageOverlay">
                                    <span>Click for more details</span>
                                </div>
                            </div>

                            <h3 className="cardTitle">{project.title}</h3>

                            <p className="cardDescription">
                                {project.description || project.bullets?.[0]}
                            </p>

                            <div className="cardTags">
                                {project.technologies?.map((tech, index) => (
                                    <span className="cardTag" key={index}>{tech}</span>
                                ))}
                            </div>

                            <div className="cardActions">
                                {(project.demo || project.github) && (
                                    <a
                                        href={project.demo || project.github}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="cardActionButton"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <i className={project.demo ? "fa-solid fa-arrow-up-right-from-square" : "fa-brands fa-github"}></i>
                                        {project.demo ? "View Demo" : "View GitHub"}
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {selectedProject && (
                <div className="modalOverlay" onClick={handleCloseModal}>
                    <div className="projectModal" onClick={(e) => e.stopPropagation()}>
                        <button className="modalClose" onClick={handleCloseModal}>
                            <i className="fa-solid fa-times"></i>
                        </button>

                        {selectedProject.image && (
                            <div className="modalImage">
                                <img src={selectedProject.image} alt={selectedProject.title} />
                            </div>
                        )}

                        <h2 className="modalTitle">{selectedProject.title}</h2>

                        <p className="modalDescription">
                            {selectedProject.description || selectedProject.bullets?.[0]}
                        </p>

                        <div className="modalTags">
                            {selectedProject.technologies?.map((tech, index) => (
                                <span className="modalTag" key={index}>{tech}</span>
                            ))}
                        </div>

                        {(selectedProject.bullets) && (
                            <div className="modalBulletsSection">
                                <h3>Details</h3>
                                <ul className="modalBullets">
                                    {(selectedProject.bullets).map((bullet, index) => (
                                        <li key={index}>{bullet}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="modalLinks">
                            {selectedProject.github && (
                                <a
                                    href={selectedProject.github}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="modalLink"
                                >
                                    <i className="fa-brands fa-github"></i> GitHub
                                </a>
                            )}
                            {(selectedProject.demo || selectedProject.live) && (
                                <a
                                    href={selectedProject.demo || selectedProject.live}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="modalLink"
                                >
                                    <i className="fa-solid fa-arrow-up-right-from-square"></i> Live Demo
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <p className="projSecFootText">
                The projects above are merely a curated highlight reel of my escapades. To shield you
                from the perils of boredom and infinite scrolling, I've spotlighted only those works,
                where my contributions were significant, not just a simple patch fix or README update.
                <br /> <br />
                Beyond this, I've contributed to a smorgasbord of college websites and portals, plus a
                forgotten attic of personal projects that either fizzled out or got eclipsed by life's
                more pressing plot twists.
                <br /> <br />
                For the unabridged saga, swing by my GitHub. But let's face it, you've probably got a life
                to live and won't even make it this far. So, cheers! :)
            </p>
        </PCLayout>
    );
};

export default ProjectsDetailPage;